import { useMutation, useQueryClient } from '@tanstack/react-query';

import { KEYS } from '~shared/api/query-keys';

import { type EmailToggle } from './get-email-toggles';
import { updateEmailToggleAPI } from './update-email-toggle';

interface UpdateAllEmailTogglesParams {
  toggles: Array<EmailToggle>;
  disabled: boolean;
}

interface ToggleUpdateResponse {
  success?: boolean;
}

export interface UpdateAllEmailTogglesResult {
  attempted: number;
  failed: number;
}

// The enable/disable endpoints return HTTP 200 with { success: false } on
// failure instead of throwing, so each response body has to be inspected.
export const updateAllEmailTogglesAPI = async ({
  toggles,
  disabled,
}: UpdateAllEmailTogglesParams): Promise<UpdateAllEmailTogglesResult> => {
  const togglesToUpdate = toggles.filter(
    (toggle) => toggle.disabled !== disabled,
  );

  const results = await Promise.allSettled(
    togglesToUpdate.map((toggle) =>
      updateEmailToggleAPI({ id: toggle.id, disable: disabled }),
    ),
  );

  const failed = results.filter(
    (result) =>
      result.status === 'rejected' ||
      (result.value as ToggleUpdateResponse | undefined)?.success === false,
  ).length;

  return { attempted: togglesToUpdate.length, failed };
};

export const useUpdateAllEmailToggles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAllEmailTogglesAPI,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [KEYS.EMAIL_TOGGLES] });
    },
  });
};
